import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { modifyURLStates } from '../utils/utils-routing';
import NavigationDropDownLink from '../components/NavigationDropDownLink';
import MaterialIcon from './MaterialIcon';
import { AgeCategory } from '../../../models/AgeCategory';
import './AgeCategorySelector.scss';
import { AppState } from '../../store/appState';
import { DropDownMenu } from './DropDownMenu';
import { useHistory } from 'react-router';
import { useProductFilters } from '../filter/useProductFilters';
import { ProductFilter } from '../../../models/Product';
import { Location, LocationState } from 'history';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { getPaginationStateObject } from './Pagination';

const AGE_CATEGORY_IDENTIFIER = 'ageCategory';

type Props = {
    readonly className?: string;
};

const ALL_AGES = Object.values(AgeCategory);

export const createAgeCategoryLink = (location: Location<LocationState>, age: string, filters: ProductFilter) => {
    const pageQueryState = getPaginationStateObject();
    const ageCategoryQueryState = { name: AGE_CATEGORY_IDENTIFIER, value: age, encode: false };

    const nextFilters = { ...filters };
    delete nextFilters['ageCategory'];

    const filterQueryState = isEmpty(nextFilters)
        ? { name: 'filter', value: undefined, encode: false }
        : { name: 'filter', value: nextFilters, encode: true };

    const search = modifyURLStates(location.search, [pageQueryState, ageCategoryQueryState, filterQueryState]);

    return `${location.pathname}?${search}`;
};

const AgeCategorySelector: FC<Props> = ({ className }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const ageCategory = useSelector((state: AppState) => state.ageCategory);
    const [expanded, setExpanded] = useState(false);
    const { filters } = useProductFilters();

    const changeAgeCategory = () => {
        setExpanded(false);
    };

    return (
        <DropDownMenu
            expanded={expanded}
            align="right"
            onToggleClick={setExpanded}
            onClickOutside={setExpanded}
            className={classNames('age-category-selector', className)}
            label={t(`filters:ageCategory.${ageCategory}`, {
                defaultValue: ageCategory
            })}
            dataDisplayname="AgeCategorySelector"
        >
            {ALL_AGES.map(age => {
                const isActive = age === ageCategory;
                const to = createAgeCategoryLink(history.location, age, filters);

                return (
                    <li key={age} className="age-category-selector-item">
                        <NavigationDropDownLink to={to} activeClassName="never-active" onClick={changeAgeCategory}>
                            <span>
                                {t(`filters:ageCategory.${age}`, {
                                    defaultValue: age
                                })}
                            </span>
                            {isActive && <MaterialIcon icon="check" primary={true} />}
                        </NavigationDropDownLink>
                    </li>
                );
            })}
        </DropDownMenu>
    );
};

export default AgeCategorySelector;
